import React, { useEffect, useState, useRef } from "react";
import { styled } from "@mui/material/styles";
import ProfileImg from "../../icons/profile_img.png";
import {
  ButtonBase,
  Grid,
  Typography as MuiTypography,
  TableContainer,
  Paper,
  Card,
  CardContent,
  TableBody,
  Table,
  TableCell as MuiTableCell,
  TableRow as MuiTableRow,
  Box,
} from "@mui/material";
import { spacing } from "@mui/system";
import moment from "moment";
import CheckIcon from "@mui/icons-material/Check";

const Typography = styled(MuiTypography)(spacing);

// Re-using the StyledCard definition with print styles
const StyledCard = styled(Card)`
  margin: 20px 0;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  @media print {
    margin: 0;
    box-shadow: none;
    overflow: visible !important;
    border: none;
  }
`;

const Img = styled("img")({
  margin: "auto",
  display: "block",
  maxWidth: "100%",
  maxHeight: "100%",
});

const TableCell = styled(MuiTableCell)`
  padding-top: 0;
  padding-bottom: 0;
`;

const TableRow = styled(MuiTableRow)`
  height: 42px;

  &:last-child th,
  &:last-child td {
    border-bottom: 0;
  }
`;

function CafFormSection({ cafForm, logoSrc, profileSrc }) {
  return (
    <StyledCard
      id="caf_form_section"
      sx={{
        "@media print": {
          pageBreakAfter: "always",
        },
      }}
    >
      <CardContent>
        <Grid container spacing={2}>
          <Grid container spacing={2} alignItems="flex-start">
            {/* LEFT: LOGO + COMPANY INFO */}
            <Grid item xs={6}>
              <ButtonBase sx={{ width: 128, height: 128 }}>
                <Img alt="logo" src={logoSrc || ProfileImg} />
              </ButtonBase>

              <Typography variant="h4">{cafForm?.mso?.tenantName}</Typography>
              <Typography variant="h4" sx={{ whiteSpace: "pre-line" }}>
                {cafForm?.msoAddress}
              </Typography>
            </Grid>

            {/* RIGHT: PROFILE IMAGE */}
            <Grid
              item
              xs={6}
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "flex-start",
              }}
            >
              <ButtonBase sx={{ width: 128, height: 128 }}>
                <Img alt="profile" src={profileSrc || ProfileImg} />
              </ButtonBase>
            </Grid>
          </Grid>
          <Grid
            container
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "flex-start",
            }}
          >
            <Typography variant="h3" sx={{ fontWeight: "bold" }}>
              CUSTOMER APPLICATION FORM
            </Typography>
          </Grid>
          <Grid item xs={12} sm container>
            <Grid item xs container direction="row" spacing={2}>
              <Grid item xs>
                <Grid container direction="column" alignItems="left" sx={{ fontSize: "20px" }}>
                  <Grid item>
                    CAF No :<span style={{ fontWeight: "bold" }}>{cafForm.cafNumber}</span>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs>
                <Grid
                  container
                  direction="column"
                  alignItems="center"
                  sx={{ fontWeight: "bold", fontSize: "20px" }}
                >
                  <Grid item>Internet Access</Grid>
                </Grid>
              </Grid>
              <Grid item xs>
                <Grid container direction="column" alignItems="end" sx={{ fontSize: "20px" }}>
                  <Grid item>
                    Date{" "}
                    <span style={{ fontWeight: "bold" }}>
                      {moment(cafForm.createdAt).format("DD-MM-YYYY")}
                    </span>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} sm container>
          <Grid item xs container direction="row" spacing={2}>
            <Grid item xs>
              <Grid container direction="column" alignItems="left" sx={{ fontSize: "20px" }}>
                <Grid item>
                  Subscriber Type{" "}
                  <Box
                    component="span"
                    sx={{
                      border: "1px solid #000",
                      padding: "4px 8px",
                      borderRadius: "4px",
                      fontWeight: "bold",
                    }}
                  >
                    {cafForm.connectionType}
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Grid container justifyContent="center" sx={{ mt: 4 }}>
          <TableContainer
            component={Paper}
            sx={{ width: "100%", border: "1px solid #ccc", borderRadius: "0px" }}
          >
            <Table size="small" sx={{ borderCollapse: "collapse" }}>
              <TableBody>
                {/* Row 1 */}
                <TableRow>
                  <TableCell
                    sx={{
                      borderRight: "1px solid grey",
                      padding: "8px",
                      width: "15%", // Set 20% width for column 1
                    }}
                  >
                    Name of Customer
                  </TableCell>
                  <TableCell
                    sx={{
                      padding: "8px",
                      width: "85%", // Set 80% width for column 2
                      fontWeight: "bold",
                    }}
                  >
                    {cafForm.fullName}
                  </TableCell>
                </TableRow>
                {/* Row 2 */}
                <TableRow>
                  <TableCell
                    sx={{
                      borderRight: "1px solid grey",
                      padding: "8px",
                      width: "15%",
                    }}
                  >
                    Customer ProfileID
                  </TableCell>
                  <TableCell
                    sx={{
                      padding: "8px",
                      width: "85%",
                      fontWeight: "bold",
                    }}
                  >
                    {cafForm.userName}
                  </TableCell>
                </TableRow>
                {/* Row 3 */}
                <TableRow>
                  <TableCell
                    sx={{
                      borderRight: "1px solid grey",
                      padding: "8px",
                      width: "15%",
                    }}
                  >
                    Billing Address
                  </TableCell>
                  <TableCell
                    sx={{
                      padding: "8px",
                      width: "85%",
                      fontWeight: "bold",
                    }}
                  >
                    {`${cafForm?.doorNo}, ${cafForm?.area}`}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          {/* Row 4 */}
          <TableContainer
            component={Paper}
            sx={{ width: "100%", border: "1px solid #ccc", borderRadius: "0px" }}
          >
            <Table size="small" sx={{ borderCollapse: "collapse" }}>
              <TableBody>
                <TableRow>
                  <TableCell
                    sx={{
                      borderRight: "1px solid grey",
                      padding: "8px",
                      width: "20%",
                    }}
                  >
                    City : <span style={{ fontWeight: "bold" }}>{cafForm.city}</span>
                  </TableCell>
                  <TableCell
                    sx={{
                      padding: "8px",
                      width: "40%",
                      borderRight: "1px solid grey",
                    }}
                  >
                    State : <span style={{ fontWeight: "bold" }}>{cafForm.state}</span>
                  </TableCell>
                  <TableCell
                    sx={{
                      padding: "8px",
                      width: "40%",
                    }}
                  >
                    Mobile : <span style={{ fontWeight: "bold" }}>{cafForm.mobileNo}</span>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <TableContainer
            component={Paper}
            sx={{ width: "100%", border: "1px solid #ccc", borderRadius: "0px" }}
          >
            <Table size="small" sx={{ borderCollapse: "collapse" }}>
              <TableBody>
                <TableRow>
                  <TableCell
                    sx={{
                      borderRight: "1px solid grey",
                      padding: "8px",
                      width: "15%",
                    }}
                  >
                    Telephone : --
                  </TableCell>
                  <TableCell
                    sx={{
                      padding: "8px",
                      width: "85%",
                    }}
                  >
                    <span style={{ fontWeight: "bold" }}>EMAIL : {cafForm.emailId}</span>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>

        <Grid container justifyContent="center" sx={{ mt: 4 }}>
          <TableContainer
            component={Paper}
            sx={{ width: "100%", border: "1px solid #ccc", borderRadius: "0px" }}
          >
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell
                    align="center"
                    sx={{
                      borderRight: "1px solid grey",
                      padding: "8px",
                      fontWeight: "bold",
                    }}
                  >
                    {cafForm.identityProofType} ID No:
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      padding: "8px",
                      fontWeight: "bold",
                    }}
                  >
                    {cafForm.identityProofNo}
                  </TableCell>
                </TableRow>
                {/* Row 2 */}
                <TableRow>
                  <TableCell
                    align="center"
                    sx={{
                      borderRight: "1px solid grey",
                      padding: "8px",
                      fontWeight: "bold",
                    }}
                  >
                    {cafForm.addressProofType} ID No:
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      padding: "8px",
                      fontWeight: "bold",
                    }}
                  >
                    {cafForm.addressProofNo}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
        <Grid
          container
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
          }}
        >
          <Typography variant="h5" sx={{ textDecoration: "underline", mt: 4 }}>
            Service Installation Address
          </Typography>
        </Grid>
        <Grid container justifyContent="center">
          <TableContainer
            component={Paper}
            sx={{ width: "100%", border: "1px solid #ccc", borderRadius: "0px" }}
          >
            <Table size="small" sx={{ borderCollapse: "collapse" }}>
              <TableBody>
                <TableRow>
                  <TableCell
                    sx={{
                      borderRight: "1px solid grey",
                      padding: "8px",
                      width: "10%",
                    }}
                  >
                    Address
                  </TableCell>
                  <TableCell
                    sx={{
                      padding: "8px",
                      width: "90%",
                    }}
                  >
                    <span style={{ fontWeight: "bold" }}>
                      {`${cafForm?.insDoorNo}, ${cafForm?.insArea}`}
                    </span>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          {/* Row 2 */}
          <TableContainer
            component={Paper}
            sx={{ width: "100%", border: "1px solid #ccc", borderRadius: "0px" }}
          >
            <Table size="small" sx={{ borderCollapse: "collapse" }}>
              <TableBody>
                <TableRow>
                  <TableCell
                    sx={{
                      borderRight: "1px solid grey",
                      padding: "8px",
                      width: "15%",
                    }}
                  >
                    City :<span style={{ fontWeight: "bold" }}>{cafForm.insCity}</span>
                  </TableCell>
                  <TableCell
                    sx={{
                      padding: "8px",
                      width: "85%",
                    }}
                  >
                    State :<span style={{ fontWeight: "bold" }}>{cafForm.insState}</span>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>

        <Grid
          container
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
          }}
        >
          <Typography variant="h5" sx={{ textDecoration: "underline", mt: 4 }}>
            Service Requirements
          </Typography>
        </Grid>
        <Grid container justifyContent="center">
          <TableContainer
            component={Paper}
            sx={{ width: "100%", border: "1px solid #ccc", borderRadius: "0px" }}
          >
            <Table size="small" sx={{ borderCollapse: "collapse" }}>
              <TableBody>
                <TableRow>
                  <TableCell
                    sx={{
                      borderRight: "1px solid grey",
                      padding: "8px",
                      width: "10%",
                    }}
                  >
                    Plan Name
                  </TableCell>
                  <TableCell
                    sx={{
                      padding: "8px",
                      width: "90%",
                    }}
                  >
                    <span style={{ fontWeight: "bold" }}>{cafForm.planName}</span>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          {/* Row 2 */}
          <TableContainer
            component={Paper}
            sx={{ width: "100%", border: "1px solid #ccc", borderRadius: "0px" }}
          >
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell
                    align="center"
                    sx={{
                      borderRight: "1px solid grey",
                      padding: "8px",
                      width: "50%",
                    }}
                  >
                    IP's Required :
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      padding: "8px",
                      width: "50%",
                    }}
                  >
                    Static IP No :- {cafForm.ipAddress}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          {/* Row 3 */}
          <TableContainer
            component={Paper}
            sx={{ width: "100%", border: "1px solid #ccc", borderRadius: "0px" }}
          >
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell
                    align="center"
                    sx={{
                      borderRight: "1px solid grey",
                      padding: "8px",
                      width: "70%",
                    }}
                  >
                    OLT Brand
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      padding: "8px",
                      width: "30%",
                    }}
                  >
                    SI.No
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <TableContainer
            component={Paper}
            sx={{ width: "100%", border: "1px solid #ccc", borderRadius: "0px" }}
          >
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell
                    align="center"
                    sx={{
                      borderRight: "1px solid grey",
                      padding: "8px",
                      width: "50%",
                    }}
                  >
                    ONU Brand
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      padding: "8px",
                      width: "50%",
                    }}
                  >
                    ONU MAC ID
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <TableContainer
            component={Paper}
            sx={{ width: "100%", border: "1px solid #ccc", borderRadius: "0px" }}
          >
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell
                    align="center"
                    sx={{
                      padding: "8px",
                    }}
                  >
                    <span style={{ fontWeight: "bold" }}> Terminating Network Equipment</span>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <TableContainer
            component={Paper}
            sx={{ width: "100%", border: "1px solid #ccc", borderRadius: "0px" }}
          >
            <Table size="small" sx={{ borderCollapse: "collapse" }}>
              <TableBody>
                <TableRow>
                  <TableCell
                    align="center"
                    sx={{
                      padding: "8px",
                      width: "25%",
                    }}
                  >
                    Router No: {cafForm.routerNo}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      padding: "8px",
                      width: "25%",
                    }}
                  >
                    Switch{" "}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      padding: "8px",
                      width: "25%",
                    }}
                  >
                    Standalone PC{" "}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      padding: "8px",
                      width: "25%",
                    }}
                  >
                    ONU{" "}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <TableContainer
            component={Paper}
            sx={{ width: "100%", border: "1px solid #ccc", borderRadius: "0px" }}
          >
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell
                    align="center"
                    sx={{
                      padding: "8px",
                    }}
                  >
                    <span style={{ fontWeight: "bold" }}> Customer Mile Details</span>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <TableContainer
            component={Paper}
            sx={{ width: "100%", border: "1px solid #ccc", borderRadius: "0px" }}
          >
            <Table size="small" sx={{ borderCollapse: "collapse" }}>
              <TableBody>
                <TableRow>
                  <TableCell
                    align="center"
                    sx={{
                      padding: "8px",
                      width: "40%",
                      borderRight: "1px solid grey",
                    }}
                  >
                    Fiber: {cafForm.customerMileType === "FIBER" && <CheckIcon />}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      padding: "8px",
                      width: "40%",
                    }}
                  >
                    RF Tower: {cafForm.customerMileType === "RF_TOWER" && <CheckIcon />}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <TableContainer
            component={Paper}
            sx={{ width: "100%", border: "1px solid #ccc", borderRadius: "0px" }}
          >
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell
                    align="center"
                    sx={{
                      borderRight: "1px solid grey",
                      padding: "8px",
                      width: "70%",
                    }}
                  >
                    Date : {moment(cafForm.activationDate).format("DD-MM-YYYY")}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      padding: "8px",
                      width: "30%",
                    }}
                  >
                    Place
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <TableContainer
            component={Paper}
            sx={{
              width: "100%",
              borderBottom: "1px solid #ccc",
              mt: 6,
              borderRadius: "0px",
            }}
          >
            <Table size="small">
              <TableBody>
                {/* Row 1 */}
                <TableRow>
                  <TableCell
                    sx={{
                      padding: "8px",
                      width: "50%", // Set 20% width for column 1
                    }}
                  >
                    Name Authorized Signatory
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      padding: "8px",
                      width: "50%", // Set 80% width for column 2
                    }}
                  >
                    Signature of Customer
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
        <Box
          sx={{
            border: "1px solid #000",
            padding: "45px",
            marginTop: "20px", // Adds top space
            width: "30%",
          }}
        >
          <i> Service Franchise</i>
        </Box>
        <Typography align="center" sx={{ fontWeight: "bold" }}>
          *** This is a Computer Generated CAF ***
        </Typography>
        <Typography align="center" variant="h4" sx={{ fontWeight: "bold", mt: "50px" }}>
          {cafForm?.mso?.tenantName}.
        </Typography>
      </CardContent>
    </StyledCard>
  );
}

export default CafFormSection;
